import * as m from "@paraglide/messages"
import type { SupportedLocale } from "@playbrain/content-types"
import { playdataButtonClassName } from "@playdata/PlaydataPrimitives"
import { useState } from "react"

/**
 * The newsletter signup.
 *
 * It used to post to `/api` — the *contact* mailer proxy — with a `type` the
 * mailer does not recognise, so every subscription fell through
 * `resolveToType()`'s default and became one email in the contact inbox. There
 * was no list: the form looked like it worked and stored nothing.
 *
 * It now posts to the newsletter's own endpoint, which writes a row. The
 * response is deliberately uniform — a new address, one already on the list
 * and one that previously reported us as spam all answer identically, so this
 * form cannot be used to find out whether an address is subscribed.
 */
export function PlaydataNewsletterForm({ locale }: { locale: SupportedLocale }) {
  const [email, setEmail] = useState("")
  /** The honeypot. Never shown, never filled by a person. */
  const [nickname, setNickname] = useState("")
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle")

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (state === "sending") return
    setState("sending")

    try {
      const response = await fetch("/api/playdata/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, locale, nickname }),
      })
      setState(response.ok ? "sent" : "failed")
      if (response.ok) setEmail("")
    } catch {
      setState("failed")
    }
  }

  if (state === "sent") {
    return (
      <p
        className="text-[1.5rem]"
        data-id="playdata-newsletter-success"
        role="status"
      >
        {m.playdata_home_newsletter_thanks({}, { locale })}
      </p>
    )
  }

  return (
    <form
      className="flex w-full max-w-[46rem] flex-col gap-[1.2rem]"
      data-id="playdata-newsletter-form"
      onSubmit={handleSubmit}
    >
      <label
        className="text-left text-[1.3rem]"
        htmlFor="playdata-newsletter-email"
      >
        {m.playdata_home_newsletter_emailLabel({}, { locale })}
      </label>
      <input
        autoComplete="email"
        className="w-full border border-[#c9cfcf] bg-play-white px-[1.2rem] py-[1rem] text-[1.5rem]"
        id="playdata-newsletter-email"
        name="email"
        onChange={(event) => setEmail(event.target.value)}
        placeholder={m.playdata_home_newsletter_placeholder({}, { locale })}
        required
        type="email"
        value={email}
      />
      {/*
       * Off-screen rather than `display: none`, because a bot that reads the
       * computed style skips a hidden field and fills a visible one. Matches
       * the contact form's arrangement.
       */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-10000px",
          top: "auto",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <label htmlFor="playdata-newsletter-nickname">Do not fill this field</label>
        <input
          autoComplete="off"
          id="playdata-newsletter-nickname"
          name="nickname"
          onChange={(event) => setNickname(event.target.value)}
          tabIndex={-1}
          type="text"
          value={nickname}
        />
      </div>
      {state === "failed" ? (
        <p
          className="text-left text-[1.3rem] text-play-pink"
          role="alert"
        >
          {m.playdata_home_newsletter_error({}, { locale })}
        </p>
      ) : null}
      <button
        className={playdataButtonClassName("primary", "self-center disabled:opacity-70")}
        disabled={state === "sending"}
        type="submit"
      >
        {state === "sending"
          ? m.playdata_home_newsletter_sending({}, { locale })
          : m.playdata_home_newsletter_submit({}, { locale })}
      </button>
    </form>
  )
}
